import React from 'react';
import styles from './admission-binder.module.scss';
import { usePageTitle } from 'src/components/app/use-page-title';
import { Tooltip } from '../../../components/core';
import update from 'immutability-helper';
import { useDrop } from 'react-dnd';
import {
  ENTITY_TYPES,
  EVENTS,
  Lang,
  REQUEST_ACCESS_ENTITIES,
  INTERNAL_ROUTING,
  ROUTES,
  CATEGORY_TYPES,
} from 'shared/constants';
import { Button, ContentBinderDrawer } from 'src/components/design-system';
import {
  AdmissionMovePopup,
  ChecklistItem,
  ChecklistItemDelete,
  ChecklistItemEditor,
} from '../admission/components';
import { AdmissionChecklistComment } from '../admission/components';
import { TagsIcons } from 'src/components/tags/tagsIcons';
import { TextTag } from 'src/components/tags/text-tags';
import { useLocation } from 'react-router';
import analyticsService from 'src/helpers/analytics.service';
import { useSelector } from 'react-redux';
import { haveEditPermissions } from 'shared/store/selectors/knowledge.selector';
import { Owners } from 'src/components/common/owners';
import admissionServiceApi from 'shared/api/admission.service.api';
import { assertPermission } from 'src/components/common/access-restricted/assert-permission';
import searchContextIdManager from 'shared/helpers/src/search-context-id-manager';
import { ListItemsNavigator } from 'src/components/common/items-navigator';

const AdmissionBinderComponent = React.memo(function AdmissionBinderComponent({
  id,
  category = {
    title: '',
    checks: [],
  },
  admissionSelectChecklist = () => {},
  initialOpenState = false,
  adminMode = false,
  updateOrder = () => {},
  langPage = Lang.ADMISSION_CHECKLIST_PAGE,
  langButtons = Lang.CHECKLIST_BUTTONS,
  langTooltip = Lang.ACTION_BUTTON_TOOLTIPS,
  createQuickCheckListItem = () => {},
  isHaveActiveChecklist = false,
}) {
  let { title, ownerIds, tags } = category;

  const hasEditPermission = useSelector(state => haveEditPermissions(state, { ownerIds }));

  const { updateSubtitle } = usePageTitle();

  React.useEffect(() => {
    updateSubtitle(title);
  }, [title, updateSubtitle]);

  const { search } = useLocation();

  const [isOpen, setOpenState] = React.useState(initialOpenState);
  const [data, setData] = React.useState(category.checks);

  const [deleteEntity, setDeleteEntity] = React.useState(null);
  const [itemEntity, setItemEntity] = React.useState(null);
  const [moveEntity, setMoveEntity] = React.useState(null);
  const [commentEntity, setCommentEntity] = React.useState(null);
  const contextId = searchContextIdManager.getContextId();

  React.useEffect(() => {
    if (id) {
      assertPermission({
        serviceMethod: async () => await admissionServiceApi.getAdmissionById({ id }),
        entity: REQUEST_ACCESS_ENTITIES.CHECKLIST,
        id,
      });
    }
  }, [id]);

  const eventData = {
    id,
    title,
    ...tags,
  };

  React.useEffect(() => {
    if (!id) return;
    analyticsService.track(EVENTS.CHECKLISTS.OPEN, {
      ...eventData,
      entityType: ENTITY_TYPES.admission,
      contextId,
    });

    analyticsService.track(EVENTS.CONTENT.OPEN, {
      ...eventData,
      entityType: ENTITY_TYPES.admission,
      contextId,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Drag and Drop
  const dndGroup = `values`;
  const [, drop] = useDrop({ accept: dndGroup });

  const dragItem = (id, atIndex) => {
    const { item, index } = findItem(id);
    setData(
      update(data, {
        $splice: [
          [index, 1],
          [atIndex, 0, item],
        ],
      }),
    );
  };

  const findItem = id => {
    const item = data.find(i => `${i.id}` === id);
    return {
      item,
      index: data.indexOf(item),
    };
  };

  const finishDrag = itemId => {
    updateOrder({
      id,
      itemId,
      index: findItem(itemId).index,
    });
  };

  // Comment
  const addCommentCheck = React.useCallback(({ id, title }) => {
    // TODO: analytics
    setCommentEntity({ id, title });
  }, []);

  const onFinishComment = React.useCallback(() => {
    setCommentEntity(null);
  }, []);

  // Delete value items
  const deleteCheck = React.useCallback(({ id, title }) => {
    // TODO: analytics
    setDeleteEntity({ id, title });
  }, []);

  const onFinishDelete = React.useCallback(() => {
    setDeleteEntity(null);
  }, []);

  // Editor
  const onFinishEditor = React.useCallback(() => {
    setItemEntity(null);
  }, []);

  const editOrCreateLabValue = React.useCallback(({ item = {} }) => {
    setItemEntity({ ...item });
  }, []);

  const moveAction = React.useCallback(({ id, title }) => {
    // TODO: analytics
    setMoveEntity({ id, title });
  }, []);

  const onFinishMove = React.useCallback(() => {
    setMoveEntity(null);
  }, []);

  const create = React.useCallback(
    ({ item = {} }) => {
      setItemEntity({ ownerIds, tags, ...item });
    },
    [ownerIds, tags],
  );

  const createQuickValue = React.useCallback(async () => {
    await createQuickCheckListItem({ categoryId: category.id, tags });
    analyticsService.track(EVENTS.CONTENT.UPLOAD, {
      entityType: ENTITY_TYPES.admissionItem,
    });
  }, [createQuickCheckListItem, category, tags]);

  const close = React.useCallback(() => {
    admissionSelectChecklist(null);
  }, [admissionSelectChecklist]);

  React.useEffect(() => {
    if (id) {
      setOpenState(true);
      setData(category.checks);
    } else {
      setOpenState(false);
    }
  }, [id, category]);

  React.useEffect(() => {
    const searchId = new URLSearchParams(search).get(INTERNAL_ROUTING.QUERY_PARAMS.CHECKLIST);

    if (!searchId && isOpen) {
      setOpenState(false);
    }
  }, [search, isOpen]);

  const renderTags = () => {
    if (!tags?.contentType && !tags?.specialty) return null;

    return (
      <div className={styles.tagsContainer}>
        <TagsIcons tags={tags} hideTags={{ targetAudience: true, contentType: true }} />
        <TextTag tag={tags?.contentType} />
      </div>
    );
  };

  const renderList = () => {
    return (
      <ListItemsNavigator orientation="vertical">
        {data?.map(item => (
          <ChecklistItem
            key={item.id}
            item={item}
            deleteValue={deleteCheck}
            editValue={editOrCreateLabValue}
            moveAction={moveAction}
            addCommentCheck={addCommentCheck}
            adminMode={adminMode}
            categoryId={id}
            canEdit={hasEditPermission}
            {...{
              dragItem,
              findItem,
              dndGroup,
              finishDrag,
            }}
          />
        ))}
      </ListItemsNavigator>
    );
  };

  return (
    <>
      <ContentBinderDrawer
        open={isOpen}
        onClose={close}
        title={title}
        shareBoxProps={{
          path: `${ROUTES.ADMISSION}?${INTERNAL_ROUTING.QUERY_PARAMS.CHECKLIST}=${id}`,
          id,
          title,
          tags,
          entityType: CATEGORY_TYPES.CHECKS,
        }}>
        <div className={styles.tagsAndOwnersWrapper}>
          <div className={styles.ownersWrapper}>
            <Owners ownerIds={ownerIds} title={langPage.VALIDATED_BY} />
          </div>
          {renderTags()}
        </div>

        {adminMode && (
          <div className={styles.buttons}>
            <Tooltip
              title={hasEditPermission ? null : langTooltip.NOT_ALLOWED_TO_EDIT}
              placement="top">
              <Button
                onClick={create}
                type="primary"
                disabled={!hasEditPermission}
                data-testid="admission-binder-add-item-button"
                size="middle">
                {langButtons.ADD}
              </Button>
            </Tooltip>
            <Tooltip
              title={hasEditPermission ? null : langTooltip.NOT_ALLOWED_TO_EDIT}
              placement="top">
              <Button
                onClick={createQuickValue}
                type="default"
                disabled={!hasEditPermission}
                size="middle">
                {langButtons.QUICK_ADD}
              </Button>
            </Tooltip>
          </div>
        )}
        <ul key={id} ref={drop}>
          {renderList()}
        </ul>
        {isHaveActiveChecklist && (
          <a href="#admission-action-button" className={styles.skipLink}>
            {langPage.SKIP_TO_ACTIONS}
          </a>
        )}
      </ContentBinderDrawer>

      <ChecklistItemDelete
        open={!!deleteEntity}
        categoryId={id}
        entity={deleteEntity}
        onFinish={onFinishDelete}
      />

      <ChecklistItemEditor
        open={!!itemEntity}
        categoryId={id}
        value={itemEntity}
        onFinish={onFinishEditor}
      />

      <AdmissionMovePopup
        isOpen={!!moveEntity}
        categoryId={id}
        item={moveEntity}
        onFinish={onFinishMove}
      />

      <AdmissionChecklistComment
        open={!!commentEntity}
        categoryId={id}
        item={commentEntity}
        onFinish={onFinishComment}
      />
    </>
  );
});

export { AdmissionBinderComponent };
